import { FC, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { IdNameList } from "../../../../api/services/common/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiRadioGroup,
  MuiSelect,
  MuiSelectWithTextInput,
  SearchSaveFiltersSubHeader,
} from "../../../../components";
import {
  MAX_LENGTH_75,
  MDM_ACTIVITY_SEARCH_TYPE_KEY,
} from "../../../../helpers/constants";
import { setApiDataToDropDownOption } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { ActivitySearchFormKey } from "../../modelsAndSchemas/models";
import { searchMdmActivitySiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { MdmActivitySearchFormWrapper } from "./MdmActivitySearchForm.style";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";

interface ActivitySearchFormProps {
  pageId: string;
  handleClearAllSearch: () => void;
  handleSearchClick: () => void;
  onRadioToggle: () => void;
  programFrameWorkData?: IdNameList[];
  projectFrameWorkData?: IdNameList[];
  disableActionBtn: boolean;
  anchorRef?: React.RefObject<HTMLButtonElement>;
}

export const MdmActivitySearchForm: FC<ActivitySearchFormProps> = (props) => {
  const {
    pageId,
    handleClearAllSearch,
    handleSearchClick,
    onRadioToggle,
    disableActionBtn,
    programFrameWorkData,
    projectFrameWorkData,
    anchorRef,
  } = props;

  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();

  const { control, setValue, getValues } = useFormContext();
  const searchTypeValue = useWatch({
    name: ActivitySearchFormKey.searchType,
    control,
  });

  const isSearchTypeProgram = useMemo(
    () => searchTypeValue === MDM_ACTIVITY_SEARCH_TYPE_KEY.program,
    [searchTypeValue],
  );
  const isSearchTypeProject = useMemo(
    () => searchTypeValue === MDM_ACTIVITY_SEARCH_TYPE_KEY.project,
    [searchTypeValue],
  );

  const {
    useFetchLabel,
    commonLabels,
    fetchComponentTitle,
    fetchDropdownValues,
  } = useSiteCore();

  const getLabel = useFetchLabel(
    searchMdmActivitySiteCoreKeys.searchActivityComponent,
  );
  const { commonARIALabel } = useNavigationSiteCore();

  const { isMobile } = useMediaQueries();

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchType = e.target.value;
    setValue(ActivitySearchFormKey.searchType, searchType);
    onRadioToggle();
  };

  const radioOptions = [
    {
      value: MDM_ACTIVITY_SEARCH_TYPE_KEY.all,
      label: getLabel(searchMdmActivitySiteCoreKeys.allRadioButton),
    },
    {
      value: MDM_ACTIVITY_SEARCH_TYPE_KEY.program,
      label: getLabel(searchMdmActivitySiteCoreKeys.programRadioButton),
    },
    {
      value: MDM_ACTIVITY_SEARCH_TYPE_KEY.project,
      label: getLabel(searchMdmActivitySiteCoreKeys.projectRadioButton),
    },
  ];

  const renderFilterSubHeader = () => {
    return (
      <SearchSaveFiltersSubHeader
        onSavedFilterClick={() => {}}
        onResetAllClick={handleClearAllSearch}
        isSavedFilterDisabled={disableActionBtn}
        anchorRef={anchorRef}
        resetButtonTestId={`${pageId}-clearAll`}
        savedFilterTestId={`${pageId}-savedFilter`}
      />
    );
  };

  return (
    <MdmActivitySearchFormWrapper>
      <AccordionHeader
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(
              searchMdmActivitySiteCoreKeys.searchActivityComponent,
            )}
          />
        }
        isExpanded={!isMobile}
      >
        {renderFilterSubHeader()}
        <form data-testid="activity-form">
          <MuiBox className="radio-container">
            <FormController
              control={control}
              name={ActivitySearchFormKey.searchType}
            >
              <MuiRadioGroup
                label={getLabel(
                  searchMdmActivitySiteCoreKeys.selectFrameworkType,
                )}
                value={getValues(ActivitySearchFormKey.searchType)}
                onChange={handleSearchTypeChange}
                options={radioOptions}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiBox>
          <MuiGrid container className="form-container">
            {isSearchTypeProgram && (
              <MuiGrid size={gridSize} className={gridSizeLargeClass}>
                <FormController
                  control={control}
                  name={ActivitySearchFormKey.programFramework}
                >
                  <MuiSelect
                    label={getLabel(
                      searchMdmActivitySiteCoreKeys.programFramework,
                    )}
                    options={
                      programFrameWorkData
                        ? setApiDataToDropDownOption(programFrameWorkData)
                        : []
                    }
                    value=""
                    id={`${pageId}-${ActivitySearchFormKey.programFramework}`}
                    visibleDefaultSelectOption
                    placeholder={commonLabels.selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            )}

            {isSearchTypeProject && (
              <MuiGrid size={gridSize} className={gridSizeLargeClass}>
                <FormController
                  control={control}
                  name={ActivitySearchFormKey.programFramework}
                >
                  <MuiSelect
                    label={getLabel(
                      searchMdmActivitySiteCoreKeys.projectFramework,
                    )}
                    options={
                      projectFrameWorkData
                        ? setApiDataToDropDownOption(projectFrameWorkData)
                        : []
                    }
                    value=""
                    id={`${pageId}-${ActivitySearchFormKey.projectFramework}`}
                    visibleDefaultSelectOption
                    placeholder={commonLabels.selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            )}

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ActivitySearchFormKey.activityType}
              >
                <MuiSelectWithTextInput
                  options={fetchDropdownValues(
                    searchMdmActivitySiteCoreKeys.searchActivityComponent,
                    searchMdmActivitySiteCoreKeys.filterByList,
                  )}
                  label={getLabel(searchMdmActivitySiteCoreKeys.activityName)}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ActivitySearchFormKey.activityTypeFilter}
                  id={`${pageId}-${ActivitySearchFormKey.activityType}`}
                  selectBoxId={`${pageId}-${ActivitySearchFormKey.activityTypeFilter}`}
                  maxLength={MAX_LENGTH_75}
                  data-testid="activity-type"
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ActivitySearchFormKey.activityStatus}
              >
                <MuiSelect
                  label={getLabel(searchMdmActivitySiteCoreKeys.activityStatus)}
                  options={fetchDropdownValues(
                    searchMdmActivitySiteCoreKeys.searchActivityComponent,
                    searchMdmActivitySiteCoreKeys.statusList,
                  )}
                  value=""
                  id={`${pageId}-${ActivitySearchFormKey.activityStatus}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  visibleDefaultSelectOption
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ActivitySearchFormKey.topic}
              >
                <MuiSelectWithTextInput
                  options={fetchDropdownValues(
                    searchMdmActivitySiteCoreKeys.searchActivityComponent,
                    searchMdmActivitySiteCoreKeys.filterByList,
                  )}
                  label={getLabel(searchMdmActivitySiteCoreKeys.topicName)}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ActivitySearchFormKey.topicFilter}
                  id={`${pageId}-${ActivitySearchFormKey.topic}`}
                  selectBoxId={`${pageId}-${ActivitySearchFormKey.topicFilter}`}
                  maxLength={MAX_LENGTH_75}
                  data-testid="topic-name"
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid container className="button-container">
            <MuiButtonSecondary onClick={() => {}} id={`${pageId}-saveFilter`}>
              {commonLabels.saveFilter}
            </MuiButtonSecondary>
            <MuiButtonPrimary
              onClick={handleSearchClick}
              id={`${pageId}-search`}
            >
              {commonLabels.search}
            </MuiButtonPrimary>
          </MuiGrid>
        </form>
      </AccordionHeader>
    </MdmActivitySearchFormWrapper>
  );
};
